import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'

import AppStore from '../../../stores/AppStore'
import withAppStore from '../../../util/withAppStore'
import { changePassword, parseJwt } from '../../../actions/user'
import LogoLarge from '../../partials/Graphics/LogoLarge'
import {
  DivAccount,
  AuthCard,
  Title,
  MessageError,
  TitleError,
  Message,
  StyledInputLight,
  Label,
  Foot,
  ButtonWhite,
} from './styledComponents'

function ChangePassword () {
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const token = searchParams.get('token')
  const intl = useIntl()

  const passwordInput = React.useRef()
  const confirmPasswordInput = React.useRef()

  const [state, setState] = React.useState({
    error: false,
    invalid: false,
    brand: AppStore.getCustomerBrand() || {},
  })

  const onChange = React.useCallback(() => {
    setBrand()
  }, [])

  const setBrand = React.useCallback(() => {
    setState(prevState => ({ ...prevState, brand: AppStore.getCustomerBrand() }))
  }, [])

  const handleErrorPassword = React.useCallback((error) => {
    setState(prevState => ({ ...prevState, error }))
  }, [])

  const handleConfirm = React.useCallback(() => {
    const { formatMessage } = intl
    const password = passwordInput.current.value || ''
    const confirmPassword = confirmPasswordInput.current.value || ''

    const long = password ? password.length : 0

    if (password && long < 8) {
      handleErrorPassword(
        formatMessage({
          id: 'auth.changePassword.invalid.useAtLeast',
          defaultMessage: 'Password should have at least 8 characters',
          description: 'Password should have at least 8 characters',
        })
      )
      return
    }

    if (password !== confirmPassword) {
      handleErrorPassword(
        formatMessage({
          id: 'auth.changePassword.invalid.noMatch',
          defaultMessage: 'Passwords do not match',
          description: 'Passwords do not match',
        })
      )
      return
    }

    // Recheck token here
    validateToken(isValid => isValid && resetPassword(password))
  }, [intl, handleErrorPassword])

  const resetPassword = React.useCallback((password) => {
    changePassword({ password }, token, () => navigate('/login'))
  }, [token, navigate])

  const validateToken = React.useCallback((done = () => {}) => {
    try {
      if (!token) { return setState(prevState => ({ ...prevState, invalid: true }), () => done(false)) }

      const parsedToken = parseJwt(token)

      if (
        parsedToken.aud !== 'https://altrac-api.com' ||
        parsedToken.exp < ((new Date()).getTime() / 1000)
      ) {
        return setState(prevState => ({ ...prevState, invalid: true }), () => done(false))
      }

      done(true)
    } catch (err) {
      console.log(err)
      // Most likely could not parse the token
      setState(prevState => ({ ...prevState, invalid: true }), () => done(false))
    }

    return true
  }, [token])

  React.useEffect(() => {
    validateToken()

    AppStore.addChangeListener(onChange)

    setBrand()

    return () => {
      try {
        AppStore.removeChangeListener(onChange)
      } catch (error) {
        console.error(error)
      }
    }
  }, [validateToken, onChange, setBrand])

  const { error, invalid, brand: { banner_svg: bannerSVG } } = state

  if (invalid) {
    return (
      <DivAccount>
        <AuthCard>
          {bannerSVG && (
            <LogoLarge
              icon={bannerSVG}
              color='#284047'
            />
          )}
          <Title>
            <FormattedMessage
              id='auth.changePassword.heading'
              defaultMessage='Change your Password'
              description='Change your Password label'
            />
          </Title>
          <Message>
            <FormattedMessage
              id='auth.changePassword.invalid.token'
              defaultMessage='Invalid token'
              description='Invalid token label'
            />
          </Message>
        </AuthCard>
      </DivAccount>
    )
  }

  return (
    <DivAccount>
      <AuthCard>
        {bannerSVG && (
          <LogoLarge
            func={() => setState(prevState => ({ ...prevState, atFront: 'LOGIN_VIEW' }))}
            icon={bannerSVG}
            color='#284047'
          />
        )}

        <Title>
          <FormattedMessage
            id='auth.changePassword.heading'
            defaultMessage='Change your Password'
            description='Change your Password label'
          />
        </Title>

        {
          error && (
            <MessageError>
              <TitleError>
                <FormattedMessage
                  id='auth.changePassword.invalid.label'
                  defaultMessage='INVALID PASSWORD'
                />
              </TitleError>
              <Message>
                {error}
              </Message>
            </MessageError>
          )
        }

        <form>
          <Label>
            <FormattedMessage
              id='auth.changePassword.new'
              defaultMessage='Choose a New Password'
              description='Choose a New Password label'
            />
            <StyledInputLight
              ref={passwordInput}
              type='password'
              autoFocus
              required
            />
          </Label>
          <Foot>
            <FormattedMessage
              id='auth.changePassword.useAtLeast'
              defaultMessage='Use at least 8 characters'
              description='Use at least 8 characters label'
            />
          </Foot>

          <Label>
            <FormattedMessage
              id='auth.changePassword.confirm'
              defaultMessage='Confirm New Password'
              description='Confirm New Password label'
            />
            <StyledInputLight
              ref={confirmPasswordInput}
              type='password'
              required
            />
          </Label>
          <Foot>
            <FormattedMessage
              id='auth.changePassword.reenter'
              defaultMessage='Reenter your password'
              description='Reenter your password label'
            />
          </Foot>
        </form>

        <ButtonWhite onClick={handleConfirm}>
          <FormattedMessage
            id='setting.save'
            defaultMessage='SAVE'
            description='SAVE label'
          />
        </ButtonWhite>
      </AuthCard>
    </DivAccount>
  )
}

export default withAppStore(ChangePassword)
